import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../components/shared/CTALink/CTALink';
import { createSelfbookHandler } from '../../utils/selfbook';
import './AccommodationsDetailTemplate.css';

export interface AccommodationSection {
  type: 'main-diptych' | 'checklist' | 'large-image' | 'scattered-collage' | 'floorplan' | 'triptych' | 'you-may-like';
  content?: any;
}

export interface AccommodationData {
  id: string;
  title: string;
  size: string;
  description: string;
  features: string[];
  sections: AccommodationSection[];
  images: {
    hero: string;
    small: string;
    gallery: string[];
    floorplan: string;
  };
  relatedRooms?: {
    id: string;
    title: string;
    description: string;
    image: string;
  }[];
}

interface AccommodationsDetailTemplateProps {
  property: 'patagonia' | 'atacama' | 'iguazu' | 'mendoza' | 'santa-catarina';
  accommodationData: Record<string, AccommodationData>;
}

const AccommodationsDetailTemplate: React.FC<AccommodationsDetailTemplateProps> = ({
  property,
  accommodationData
}) => {
  const { roomType } = useParams<{ roomType: string }>();
  const navigate = useNavigate();
  const { setHoveredDestination } = useOverlayManager();
  const handleBooking = createSelfbookHandler(navigate, `/${property}/contact`);

  useEffect(() => {
    initializeAllFeatures();
    const carouselCleanup = initializeDragScroll('accommodation-detail-carousel');
    setHoveredDestination(null);
    
    return () => {
      if (typeof carouselCleanup === 'function') carouselCleanup();
    };
  }, [setHoveredDestination]);

  const currentRoom = accommodationData[roomType as keyof typeof accommodationData] || Object.values(accommodationData)[0];

  const handleBackClick = () => {
    navigate(`/${property}/accommodations`);
  };

  const handleBookNow = () => {
    // Navigate to contact page for booking
    navigate('/contact');
  };

  const renderSection = (section: AccommodationSection, index: number) => {
    switch (section.type) {
      case 'main-diptych':
        return (
          <div key={index} className="detail-section main-diptych">
            <div className="diptych-content">
              <div className="text-block">
                <button className="back-button" onClick={handleBackClick}>
                  <div className="back-arrow" />
                  <span>BACK</span>
                </button>
                <h1 className="room-title">{currentRoom.title}</h1>
                <div className="room-size">{currentRoom.size}</div>
                <p className="room-description">{currentRoom.description}</p>
                <HandDrawnButton
                  text="BOOK NOW"
                  onClick={handleBookNow}
                  variant={property}
                  color="awasi"
                  animation="stroke-to-fill"
                  size="normal"
                  className="accommodation-book-button"
                />
              </div>
              <div className="hero-image">
                <img src={currentRoom.images.hero} alt={currentRoom.title} />
              </div>
            </div>
          </div>
        );

      case 'checklist':
        return (
          <div key={index} className="detail-section checklist-section">
            <div className="checklist-content">
              <ul className="features-list">
                {currentRoom.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className="small-image">
                <img src={currentRoom.images.small} alt="Room detail" />
              </div>
            </div>
          </div>
        );

      case 'large-image':
        return (
          <div key={index} className="detail-section large-image-section">
            <img src={section.content?.image || currentRoom.images.gallery[0]} alt="Room view" />
          </div>
        );

      case 'scattered-collage':
        return (
          <div key={index} className="detail-section scattered-collage">
            <div className="collage-container">
              {currentRoom.images.gallery.slice(0, 4).map((img, idx) => (
                <div key={idx} className={`collage-image image-${idx + 1}`}>
                  <img src={img} alt={`Room detail ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        );

      case 'floorplan':
        return (
          <div key={index} className="detail-section floorplan-section">
            <div className="floorplan-content">
              <div className="floorplan-text">
                <p className="floorplan-description">
                  {section.content?.description || 'Experience thoughtfully designed spaces that blend luxury with the natural environment.'}
                </p>
                <HandDrawnButton
                  text="BOOK NOW"
                  onClick={handleBookNow}
                  variant={property}
                  color="awasi"
                  animation="stroke-to-stroke"
                  size="normal"
                  className="accommodation-book-button-outline"
                />
              </div>
              <div className="floorplan-image">
                <img src={currentRoom.images.floorplan} alt="Room floorplan" />
              </div>
            </div>
          </div>
        );

      case 'triptych':
        return (
          <div key={index} className="detail-section triptych-section">
            <div className="triptych-container">
              {currentRoom.images.gallery.slice(-3).map((img, idx) => (
                <div key={idx} className="triptych-image">
                  <img src={img} alt={`Gallery ${idx + 1}`} />
                </div>
              ))}
            </div>
          </div>
        );

      case 'you-may-like':
        return (
          <div key={index} className="detail-section you-may-like-section">
            <div className="section-header">
              <h2>You may also like</h2>
            </div>
            {currentRoom.relatedRooms && (
              <div className="slideshow-room-card">
                <div className="room-image-container">
                  <img src={currentRoom.relatedRooms[0].image} alt={currentRoom.relatedRooms[0].title} />
                  <div className="carousel-arrows">
                    <button className="arrow-left" aria-label="Previous room" />
                    <button className="arrow-right" aria-label="Next room" />
                  </div>
                </div>
                <div className="room-details">
                  <h3>{currentRoom.relatedRooms[0].title}</h3>
                  <p>{currentRoom.relatedRooms[0].description}</p>
                  <div className="room-links">
                    <CTALink
                      text="BOOK YOUR STAY"
                      type="primary"
                      color="awasi"
                      onClick={handleBooking}
                      className="accommodation-related-primary-link"
                    />
                    <CTALink
                      text="SEE DETAILS"
                      type="secondary"
                      color="awasi"
                      onClick={() => navigate(`/${property}/accommodations/${currentRoom.relatedRooms![0].id}`)}
                      className="accommodation-related-secondary-link"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`property-page accommodation-detail-template accommodation-detail-${property}`}>
      {/* Navigation */}
      <UnifiedNavigation variant="light" destination={property} />
      
      {/* Horizontal Scrolling Carousel */}
      <section className="accommodation-detail-carousel-container">
        <div 
          className="accommodation-detail-carousel"
          id="accommodation-detail-carousel"
        >
          {currentRoom.sections.map((section, index) => renderSection(section, index))}
        </div>
      </section>

      {/* Instagram Section */}
      <InstagramSection />

      {/* Footer */}
      <Footer variant={property} />
    </div>
  );
};

export default AccommodationsDetailTemplate;